import React, { useState, useEffect } from 'react'
import { 
  Plus, 
  Search, 
  Filter, 
  Edit2, 
  Eye, 
  Power,
  Building2,
  Mail,
  Phone,
  Globe,
  GraduationCap,
  Users,
  UserPlus
} from 'lucide-react'
import { Agency, LegalEntityType } from '@/types'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import Pagination from '@/components/ui/Pagination'
import { formatDate, cn } from '@/lib/utils'
import { api } from '@/lib/api'
import ImpersonateButton from '@/components/impersonation/ImpersonateButton'
import { MultiSelect } from '@/components/ui/multi-select-combobox'

const LEGAL_ENTITY_LABELS: Record<string, string> = {
  PROPRIETORSHIP: 'Proprietorship',
  PARTNERSHIP: 'Partnership',
  LLP: 'LLP',
  PVT_LTD: 'Pvt Ltd',
  HUF: 'HUF',
  OTHER: 'Other',
}

/* Unused interface - commented to fix build
interface AgencyCardProps {
  agency: Agency
  onEdit: (agency: Agency) => void
  onView: (agency: Agency) => void
  onToggleStatus: (agency: Agency) => void
}
*/

/* Unused component - commented to fix build
const AgencyCard: React.FC<AgencyCardProps> = ({ 
  agency, 
  onEdit, 
  onView, 
  onToggleStatus 
}) => {
  const [showDropdown, setShowDropdown] = useState(false)

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="h-12 w-12 bg-blue-500 rounded-lg flex items-center justify-center">
            <Building2 className="h-6 w-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{agency.name}</h3>
            <div className="flex items-center space-x-2 mt-1">
              <span className={cn(
                'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                agency.status === 'ACTIVE' 
                  ? 'bg-green-100 text-green-800' 
                  : agency.status === 'PENDING'
                  ? 'bg-yellow-100 text-yellow-800'
                  : agency.status === 'SUSPENDED'
                  ? 'bg-orange-100 text-orange-800'
                  : 'bg-red-100 text-red-800'
              )}>
                {agency.status || 'Unknown'}
              </span>
              {agency.isVerified && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  Verified
                </span>
              )}
            </div>
          </div>
        </div>
        
        <div className="relative">
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <MoreHorizontal className="h-4 w-4 text-gray-500" />
          </button>
          
          {showDropdown && (
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-10">
              <div className="py-1">
                <button
                  onClick={() => {
                    onView(agency)
                    setShowDropdown(false)
                  }}
                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                >
                  <Eye className="h-4 w-4 mr-2" />
                  View Details
                </button>
                <button
                  onClick={() => {
                    onEdit(agency)
                    setShowDropdown(false)
                  }}
                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                >
                  <Edit2 className="h-4 w-4 mr-2" />
                  Edit
                </button>
                <button
                  onClick={() => {
                    onToggleStatus(agency)
                    setShowDropdown(false)
                  }}
                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                >
                  <Power className="h-4 w-4 mr-2" />
                  {agency.status === 'ACTIVE' ? 'Suspend' : 'Activate'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-2 text-sm text-gray-600 mb-4">
        {agency.description && (
          <p className="line-clamp-2">{agency.description}</p>
        )}
        
        <div className="flex items-center space-x-4">
          {(agency.email || agency.contactEmail) && (
            <div className="flex items-center">
              <Mail className="h-4 w-4 mr-1" />
              <span className="truncate">{agency.email || agency.contactEmail}</span>
            </div>
          )}
          {(agency.phone || agency.contactPhone) && (
            <div className="flex items-center">
              <Phone className="h-4 w-4 mr-1" />
              <span>{agency.phone || agency.contactPhone}</span>
            </div>
          )}
        </div>
        
        {agency.website && (
          <div className="flex items-center">
            <Globe className="h-4 w-4 mr-1" />
            <a 
              href={agency.website} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline truncate"
            >
              {agency.website}
            </a>
          </div>
        )}
      </div>

      <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-100">
        <div className="text-center">
          <div className="text-lg font-semibold text-gray-900">
            {formatNumber(agency.totalCounselors || agency.counselorsCount || 0)}
          </div>
          <div className="text-xs text-gray-500">Counselors</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-semibold text-gray-900">
            {formatNumber(agency.activeToday || 0)}
          </div>
          <div className="text-xs text-gray-500">Active Today</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-semibold text-gray-900">
            {agency.isVerified ? 'Yes' : 'No'}
          </div>
          <div className="text-xs text-gray-500">Verified</div>
        </div>
      </div>

      <div className="mt-4 text-xs text-gray-500">
        Created: {formatDate(agency.createdAt)}
      </div>
    </div>
  )
}
*/

const AgencyManagement: React.FC = () => {
  const [agencies, setAgencies] = useState<Agency[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive' | 'pending' | 'suspended'>('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalAgencies, setTotalAgencies] = useState(0)
  const [error, setError] = useState('')
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [selectedAgency, setSelectedAgency] = useState<Agency | null>(null)

  const fetchAgencies = async (page = 1, search = '', status = 'all') => {
    try {
      setLoading(true)
      const params: any = {
        page,
        limit: 12,
      }
      
      if (search) params.search = search
      if (status !== 'all') params.status = status

      console.log('Fetching agencies with params:', params)
      
      // Use centralized API call
      const response = await api.get<{data: Agency[], meta: any}>(`/admin/agencies?${new URLSearchParams(params)}`)
      console.log('Agency API response:', response)
      
      // The backend returns {statusCode, message, data, meta} structure
      setAgencies(response.data || [])
      setTotalPages(response.meta?.lastPage || 1)
      setTotalAgencies(response.meta?.total ?? 0)
      setError('')
    } catch (error) {
      console.error('Failed to fetch agencies:', error)
      setError(`Failed to load agencies: ${error instanceof Error ? error.message : 'Unknown error'}`)
      
      // For development/testing, show some mock data when API fails
      const mockAgencies: Agency[] = [
        {
          id: '1',
          name: 'Elite Education Counseling',
          email: 'info@eliteedu.com',
          phone: '+1234567890',
          address: '123 Education St, Learning City',
          description: 'Premier educational counseling services for students worldwide.',
          status: 'ACTIVE',
          isActive: true,
          isVerified: true,
          website: 'https://eliteedu.com',
          totalCounselors: 25,
          activeToday: 18,
          createdAt: '2024-01-15T10:00:00Z',
          updatedAt: '2024-01-20T15:30:00Z'
        },
        {
          id: '2', 
          name: 'Future Guidance Partners',
          email: 'contact@futuregh.com',
          phone: '+1987654321',
          address: '456 Career Ave, Success Town',
          description: 'Specialized in career guidance and institution preparation.',
          status: 'PENDING',
          isActive: true,
          isVerified: false,
          website: 'https://futuregh.com',
          totalCounselors: 12,
          activeToday: 8,
          createdAt: '2024-02-01T09:00:00Z',
          updatedAt: '2024-02-05T11:20:00Z'
        }
      ]
      
      setAgencies(mockAgencies)
      setTotalPages(1)
      setTotalAgencies(mockAgencies.length)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAgencies(currentPage, searchTerm, filterStatus)
  }, [currentPage, searchTerm, filterStatus])

  const handleSearch = (value: string) => {
    setSearchTerm(value)
    setCurrentPage(1)
  }

  const handleFilterChange = (status: 'all' | 'active' | 'inactive' | 'pending' | 'suspended') => {
    setFilterStatus(status)
    setCurrentPage(1)
  }

  const handleAddAgency = () => {
    setSelectedAgency(null)
    setShowAddModal(true)
  }

  const handleEditAgency = (agency: Agency) => {
    setSelectedAgency(agency)
    setShowEditModal(true)
  }

  const handleViewAgency = (agency: Agency) => {
    setSelectedAgency(agency)
    setShowViewModal(true)
  }

  const handleToggleStatus = async (agency: Agency) => {
    // Convert uppercase to lowercase status values that backend expects
    const currentStatus = agency.status.toLowerCase()
    const newStatus = currentStatus === 'active' ? 'suspended' : 'active'
    const action = newStatus === 'suspended' ? 'suspend' : 'activate'
    
    if (!confirm(`Are you sure you want to ${action} "${agency.name}"?`)) {
      return
    }

    try {
      await api.patch(`/admin/agencies/${agency.id}/status`, { status: newStatus })
      // Refresh the list
      fetchAgencies(currentPage, searchTerm, filterStatus)
    } catch (error: any) {
      console.error('Failed to toggle agency status:', error)
      setError(error.message || 'Failed to update agency status')
    }
  }

  return (
    <div className="gap-y-6 flex flex-col">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Agency Management</h1>
        <button
          onClick={handleAddAgency}
          className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 flex items-center space-x-2 transition-colors"
        >
          <Plus className="h-4 w-4" />
          <span>Add Agency</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search agencies..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <select
              value={filterStatus}
              onChange={(e) => handleFilterChange(e.target.value as any)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              {/* <option value="inactive">Inactive</option> */}
              {/* <option value="pending">Pending</option> */}
              <option value="suspended">Suspended</option>
            </select>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-800 text-sm">{error}</div>
        </div>
      )}

      {/* Agencies Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <LoadingSpinner size="lg" />
          </div>
        ) : agencies.length === 0 ? (
          <div className="p-12 text-center">
            <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No agencies found</h3>
            <p className="text-gray-500 mb-4">
              {searchTerm || filterStatus !== 'all'
                ? 'Try adjusting your search or filter criteria.' 
                : 'Get started by adding your first agency.'}
            </p>
            {(!searchTerm && filterStatus === 'all') && (
              <button
                onClick={handleAddAgency}
                className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 inline-flex items-center space-x-2"
              >
                <Plus className="h-4 w-4" />
                <span>Add First Agency</span>
              </button>
            )}
          </div>
        ) : (
          <>
            <ul className="divide-y divide-gray-200 md:hidden">
              {agencies.map((agency) => (
                <li key={agency.id} className="p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 h-10 w-10">
                      {agency.logoUrl ? (
                        <img
                          className="h-10 w-10 rounded-lg object-cover"
                          src={agency.logoUrl}
                          alt={agency.name}
                        />
                      ) : (
                        <div className="h-10 w-10 bg-primary rounded-lg flex items-center justify-center">
                          <Building2 className="h-5 w-5 text-white" />
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">{agency.name}</p>
                      {agency.website && (
                        <a
                          href={agency.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-gray-500 hover:text-primary flex items-center gap-1 truncate"
                        >
                          <Globe className="h-3 w-3 shrink-0" />
                          Website
                        </a>
                      )}
                    </div>
                    <span
                      className={cn(
                        'shrink-0 inline-flex px-2 py-0.5 text-xs font-semibold rounded-full',
                        agency.status === 'ACTIVE'
                          ? 'bg-green-100 text-green-800'
                          : agency.status === 'PENDING'
                            ? 'bg-yellow-100 text-yellow-800'
                            : agency.status === 'SUSPENDED'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-gray-100 text-gray-800',
                      )}
                    >
                      {agency.status}
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-gray-600">
                    <span className="flex items-center gap-1 truncate">
                      <Mail className="h-3 w-3 text-gray-400 shrink-0" />
                      {agency.email}
                    </span>
                    {agency.phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="h-3 w-3 text-gray-400 shrink-0" />
                        {agency.phone}
                      </span>
                    )}
                    <span>{agency.totalCounselors || 0} counselors</span>
                    <span>Created {formatDate(agency.createdAt)}</span>
                  </div>

                  <div className="flex items-center justify-end gap-2 pt-1">
                    <button
                      onClick={() => handleViewAgency(agency)}
                      className="p-1.5 rounded-md text-gray-600 hover:bg-gray-100"
                      aria-label="View Details"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleEditAgency(agency)}
                      className="p-1.5 rounded-md text-indigo-600 hover:bg-indigo-50"
                      aria-label="Edit"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <ImpersonateButton
                      userId={agency.adminUserId ?? undefined}
                      name={agency.ownerName || agency.name}
                      label={`View as ${agency.name}'s admin`}
                    />
                    <button
                      onClick={() => handleToggleStatus(agency)}
                      className={cn(
                        'p-1.5 rounded-md',
                        agency.status === 'ACTIVE'
                          ? 'text-red-600 hover:bg-red-50'
                          : 'text-green-600 hover:bg-green-50',
                      )}
                      aria-label={agency.status === 'ACTIVE' ? 'Suspend' : 'Activate'}
                    >
                      <Power className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>

            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Agency
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Counselors
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {agencies.map((agency) => (
                    <tr key={agency.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            {agency.logoUrl ? (
                              <img 
                                className="h-10 w-10 rounded-lg object-cover" 
                                src={agency.logoUrl} 
                                alt={agency.name}
                              />
                            ) : (
                              <div className="h-10 w-10 bg-primary rounded-lg flex items-center justify-center">
                                <Building2 className="h-5 w-5 text-white" />
                              </div>
                            )}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {agency.name}
                            </div>
                            {agency.website && (
                              <div className="text-sm text-gray-500 flex items-center">
                                <Globe className="h-3 w-3 mr-1" />
                                <a 
                                  href={agency.website} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="hover:text-primary"
                                >
                                  Website
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          <div className="flex items-center mb-1">
                            <Mail className="h-3 w-3 mr-1 text-gray-400" />
                            <span className="truncate max-w-[130px]" title={agency.email}>{agency.email}</span>
                          </div>
                          {agency.phone && (
                            <div className="flex items-center">
                              <Phone className="h-3 w-3 mr-1 text-gray-400" />
                              <span>{agency.phone}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={cn(
                          'inline-flex px-2 py-1 text-xs font-semibold rounded-full',
                          agency.status === 'ACTIVE' 
                            ? 'bg-green-100 text-green-800'
                            : agency.status === 'PENDING'
                            ? 'bg-yellow-100 text-yellow-800'
                            : agency.status === 'SUSPENDED'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-gray-100 text-gray-800'
                        )}>
                          {agency.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {agency.totalCounselors || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(agency.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleViewAgency(agency)}
                            className="text-gray-600 hover:text-gray-900 p-1 rounded"
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleEditAgency(agency)}
                            className="text-indigo-600 hover:text-indigo-900 p-1 rounded"
                            title="Edit"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <ImpersonateButton
                            userId={agency.adminUserId ?? undefined}
                            name={agency.ownerName || agency.name}
                            label={`View as ${agency.name}'s admin`}
                          />
                          <button
                            onClick={() => handleToggleStatus(agency)}
                            className={cn(
                              'p-1 rounded',
                              agency.status === 'ACTIVE' 
                                ? 'text-red-600 hover:text-red-900' 
                                : 'text-green-600 hover:text-green-900'
                            )}
                            title={agency.status === 'ACTIVE' ? 'Suspend' : 'Activate'}
                          >
                            <Power className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
              <Pagination
                page={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                total={totalAgencies}
                pageSize={12}
                itemLabel="agencies"
              />
            </div>
          </>
        )}
      </div>

      {/* Add Agency Modal */}
      {showAddModal && (
        <AgencyModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false)
            fetchAgencies(currentPage, searchTerm, filterStatus)
          }}
          title="Add New Agency"
        />
      )}

      {/* Edit Agency Modal */}
      {showEditModal && selectedAgency && (
        <AgencyModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false)
            fetchAgencies(currentPage, searchTerm, filterStatus)
          }}
          agency={selectedAgency}
          title="Edit Agency"
        />
      )}

      {/* View Agency Modal */}
      {showViewModal && selectedAgency && (
        <AgencyViewModal
          isOpen={showViewModal}
          onClose={() => setShowViewModal(false)}
          agency={selectedAgency}
          onChanged={() => fetchAgencies(currentPage, searchTerm, filterStatus)}
        />
      )}
    </div>
  )
}

// Agency Form Modal Component
interface AgencyModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  agency?: Agency
  title: string
}

const AgencyModal: React.FC<AgencyModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  agency,
  title
}) => {
  const [formData, setFormData] = useState({
    name: '',
    ownerName: '',
    email: '',
    phone: '',
    alternatePhone: '',
    address: '',
    pincode: '',
    legalEntityType: '' as LegalEntityType | '',
    description: '',
    website: '',
    specialties: [] as string[]
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formError, setFormError] = useState('')

  // Initialize form data when agency is provided (edit mode)
  useEffect(() => {
    if (agency) {
      setFormData({
        name: agency.name || '',
        ownerName: agency.ownerName || '',
        email: agency.email || '',
        phone: agency.phone || '',
        alternatePhone: agency.alternatePhone || '',
        address: agency.address || '',
        pincode: agency.pincode || '',
        legalEntityType: agency.legalEntityType || '',
        description: agency.description || '',
        website: agency.website || '',
        specialties: agency.specialties || []
      })
    }
  }, [agency])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setFormError('')

    try {
      // class-validator's @IsOptional only skips validation for `undefined`,
      // not '' — an unselected dropdown must be omitted, not sent as ''.
      const payload = {
        ...formData,
        legalEntityType: formData.legalEntityType || undefined,
      }
      if (agency) {
        // Update existing agency
        await api.patch(`/admin/agencies/${agency.id}`, payload)
      } else {
        // Create new agency
        await api.post('/admin/agencies', payload)
      }
      onSuccess()
    } catch (error: any) {
      setFormError(error.message || 'Failed to save agency')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 m-auto">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>

          {formError && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
              <div className="text-red-800 text-sm">{formError}</div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Agency Name *
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Owner Name
              </label>
              <input
                type="text"
                value={formData.ownerName}
                onChange={(e) => setFormData({ ...formData, ownerName: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email (used as login ID) *
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
              <p className="mt-1 text-xs text-gray-500">
                This email becomes the agency's login — they'll sign in here with a one-time code, no password needed.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mobile Number
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                <p className="mt-1 text-xs text-gray-500">Also used as the account's phone number for login.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Alternate No.
                </label>
                <input
                  type="tel"
                  value={formData.alternatePhone}
                  onChange={(e) => setFormData({ ...formData, alternatePhone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address
              </label>
              <textarea
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pin Code
                </label>
                <input
                  type="text"
                  value={formData.pincode}
                  onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Legal Entity
                </label>
                <select
                  value={formData.legalEntityType}
                  onChange={(e) =>
                    setFormData({ ...formData, legalEntityType: e.target.value as LegalEntityType | '' })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white"
                >
                  <option value="">Select...</option>
                  <option value="PROPRIETORSHIP">Proprietorship</option>
                  <option value="PARTNERSHIP">Partnership</option>
                  <option value="LLP">LLP</option>
                  <option value="PVT_LTD">Pvt Ltd</option>
                  <option value="HUF">HUF</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Website
              </label>
              <input
                type="url"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            <div className="flex justify-end space-x-3 pt-6">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50"
              >
                {isSubmitting ? 'Saving...' : agency ? 'Update Agency' : 'Create Agency'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

// Agency View Modal Component
interface AgencyViewModalProps {
  isOpen: boolean
  onClose: () => void
  agency: Agency
  /** Called after counselors were assigned, so the list behind the modal can refresh its counts. */
  onChanged?: () => void
}

interface LinkedCounselor {
  id: string
  fullName?: string
  firstName?: string
  lastName?: string
  email?: string
  specializations?: string[]
  isAvailable?: boolean
  isActive?: boolean
  user?: { name?: string | null; email?: string | null }
}

interface LinkedCollege {
  id: string
  name: string
  location?: string | null
  state?: string | null
  isActive: boolean
  priority: number
}

const AgencyViewModal: React.FC<AgencyViewModalProps> = ({
  isOpen,
  onClose,
  agency,
  onChanged
}) => {
  const [colleges, setColleges] = useState<LinkedCollege[]>([])
  const [counselors, setCounselors] = useState<LinkedCounselor[]>([])
  const [loadingLinks, setLoadingLinks] = useState(false)
  const [collegesError, setCollegesError] = useState('')
  const [counselorsError, setCounselorsError] = useState('')
  const [showAssign, setShowAssign] = useState(false)
  const [unassigned, setUnassigned] = useState<LinkedCounselor[]>([])
  const [loadingUnassigned, setLoadingUnassigned] = useState(false)
  const [selectedToAssign, setSelectedToAssign] = useState<string[]>([])
  const [assigning, setAssigning] = useState(false)
  const [assignResult, setAssignResult] = useState<{ assigned: number; skipped: { id: string; reason: string }[] } | null>(null)
  const [assignError, setAssignError] = useState('')

  useEffect(() => {
    if (!isOpen || !agency?.id) return
    let cancelled = false

    const asArray = (payload: any) =>
      Array.isArray(payload) ? payload : (payload?.data ?? [])

    const load = async () => {
      setLoadingLinks(true)
      setCollegesError('')
      setCounselorsError('')

      const [collegesResult, counselorsResult] = await Promise.allSettled([
        api.get<any>(`/agencies/${agency.id}/colleges`),
        api.get<any>(`/agencies/${agency.id}/counselors?page=1&limit=100`),
      ])
      if (cancelled) return

      if (collegesResult.status === 'fulfilled') {
        setColleges(asArray(collegesResult.value))
      } else {
        setCollegesError(collegesResult.reason?.message || 'Could not load linked institutions')
      }

      if (counselorsResult.status === 'fulfilled') {
        setCounselors(asArray(counselorsResult.value))
      } else {
        setCounselorsError(counselorsResult.reason?.message || 'Could not load counselors')
      }

      setLoadingLinks(false)
    }

    void load()
    return () => { cancelled = true }
  }, [isOpen, agency?.id])

  const asList = (payload: any) => (Array.isArray(payload) ? payload : (payload?.data ?? []))

  const counselorDisplayName = (c: LinkedCounselor) =>
    c.fullName ||
    [c.firstName, c.lastName].filter(Boolean).join(' ') ||
    c.user?.name ||
    c.email ||
    c.user?.email ||
    'Unnamed counselor'

  const loadUnassigned = async () => {
    const res = await api.get<any>('/admin/counselors?unassigned=true&page=1&limit=100')
    setUnassigned(asList(res))
  }

  const openAssign = async () => {
    setShowAssign(true)
    setAssignResult(null)
    setAssignError('')
    setSelectedToAssign([])
    setLoadingUnassigned(true)
    try {
      await loadUnassigned()
    } catch (e) {
      setAssignError(e instanceof Error ? e.message : 'Could not load counselors')
    } finally {
      setLoadingUnassigned(false)
    }
  }

  const handleAssign = async () => {
    if (selectedToAssign.length === 0 || assigning) return
    setAssigning(true)
    setAssignError('')
    try {
      const result = await api.post<{ assigned: number; skipped: { id: string; reason: string }[] }>(
        `/admin/agencies/${agency.id}/counselors`,
        { counselorIds: selectedToAssign },
      )
      setAssignResult(result)
      setSelectedToAssign([])

      // Refresh this agency's roster and the picker, then the list behind the modal.
      const [roster] = await Promise.allSettled([
        api.get<any>(`/agencies/${agency.id}/counselors?page=1&limit=100`),
        loadUnassigned(),
      ])
      if (roster.status === 'fulfilled') setCounselors(asList(roster.value))
      onChanged?.()
    } catch (e) {
      setAssignError(e instanceof Error ? e.message : 'Could not assign counselors')
    } finally {
      setAssigning(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 m-auto">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Agency Details</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>

          <div className="space-y-6">
            <div className="flex items-center space-x-4">
              <div className="h-16 w-16 bg-blue-500 rounded-lg flex items-center justify-center">
                <Building2 className="h-8 w-8 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{agency.name}</h3>
                <div className="flex items-center space-x-2 mt-1">
                  <span className={cn(
                    'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                    agency.status === 'ACTIVE' 
                      ? 'bg-green-100 text-green-800' 
                      : agency.status === 'PENDING'
                      ? 'bg-yellow-100 text-yellow-800'
                      : agency.status === 'SUSPENDED'
                      ? 'bg-orange-100 text-orange-800'
                      : 'bg-red-100 text-red-800'
                  )}>
                    {agency.status}
                  </span>
                  {agency.isVerified && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      Verified
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Owner Name</label>
                <p className="text-sm text-gray-900">{agency.ownerName || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email (login ID)</label>
                <p className="text-sm text-gray-900">{agency.email || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number</label>
                <p className="text-sm text-gray-900">{agency.phone || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Alternate No.</label>
                <p className="text-sm text-gray-900">{agency.alternatePhone || 'N/A'}</p>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <p className="text-sm text-gray-900">{agency.address || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pin Code</label>
                <p className="text-sm text-gray-900">{agency.pincode || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Legal Entity</label>
                <p className="text-sm text-gray-900">{LEGAL_ENTITY_LABELS[agency.legalEntityType || ''] || 'N/A'}</p>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <p className="text-sm text-gray-900">{agency.description || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                {agency.website ? (
                  <a 
                    href={agency.website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline"
                  >
                    {agency.website}
                  </a>
                ) : (
                  <p className="text-sm text-gray-900">N/A</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Specialties</label>
                <div className="flex flex-wrap gap-1">
                  {agency.specialties && agency.specialties.length > 0 ? (
                    agency.specialties.map((specialty, index) => (
                      <span 
                        key={index}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                      >
                        {specialty}
                      </span>
                    ))
                  ) : (
                    <p className="text-sm text-gray-900">N/A</p>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-200">
              <div className="text-center">
                <div className="text-lg font-semibold text-gray-900">
                  {agency.totalCounselors || 0}
                </div>
                <div className="text-sm text-gray-500">Counselors</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-semibold text-gray-900">
                  {agency.activeToday || 0}
                </div>
                <div className="text-sm text-gray-500">Active Today</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-semibold text-gray-900">
                  {agency.isVerified ? 'Yes' : 'No'}
                </div>
                <div className="text-sm text-gray-500">Verified</div>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <h4 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-1.5">
                <GraduationCap className="h-4 w-4" />
                Linked Institutions
                {colleges.length > 0 && (
                  <span className="text-xs font-normal text-gray-500">
                    ({colleges.length})
                  </span>
                )}
              </h4>

              {loadingLinks ? (
                <p className="text-sm text-gray-400">Loading…</p>
              ) : collegesError ? (
                <p className="text-sm text-red-500">{collegesError}</p>
              ) : colleges.length > 0 ? (
                <div className="border border-gray-200 rounded-md divide-y divide-gray-100 bg-white overflow-hidden max-h-56 overflow-y-auto">
                  {colleges.map((college) => (
                    <div key={college.id} className="flex items-center gap-2 px-3 py-2 text-sm">
                      <span className="flex-1 min-w-0">
                        <span className="text-gray-900">{college.name}</span>
                        {(college.location || college.state) && (
                          <span className="block text-xs text-gray-500 truncate">
                            {[college.location, college.state].filter(Boolean).join(', ')}
                          </span>
                        )}
                      </span>
                      {!college.isActive && (
                        <span className="text-xs text-red-500 shrink-0">(inactive)</span>
                      )}
                      <span
                        title={`This agency is #${college.priority + 1} in ${college.name}'s agency order`}
                        className={cn(
                          'shrink-0 text-xs rounded px-1.5 py-0.5',
                          college.priority === 0
                            ? 'bg-primary/10 text-primary font-medium'
                            : 'bg-gray-100 text-gray-500',
                        )}
                      >
                        #{college.priority + 1}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400">No institutions linked yet.</p>
              )}
            </div>

            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <h4 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-1.5">
                <Users className="h-4 w-4" />
                Counselors
                {counselors.length > 0 && (
                  <span className="text-xs font-normal text-gray-500">
                    ({counselors.length})
                  </span>
                )}
              </h4>

              {loadingLinks ? (
                <p className="text-sm text-gray-400">Loading…</p>
              ) : counselorsError ? (
                <p className="text-sm text-red-500">{counselorsError}</p>
              ) : counselors.length > 0 ? (
                <div className="border border-gray-200 rounded-md divide-y divide-gray-100 bg-white overflow-hidden max-h-56 overflow-y-auto">
                  {counselors.map((counselor) => {
                    const name =
                      counselor.fullName ||
                      [counselor.firstName, counselor.lastName].filter(Boolean).join(' ') ||
                      counselor.user?.name ||
                      counselor.email ||
                      counselor.user?.email ||
                      'Unnamed counselor'
                    return (
                      <div
                        key={counselor.id}
                        className="flex items-center gap-2 px-3 py-2 text-sm"
                      >
                        <span className="flex-1 min-w-0">
                          <span className="text-gray-900">{name}</span>
                          {!!counselor.specializations?.length && (
                            <span className="block text-xs text-gray-500 truncate">
                              {counselor.specializations.join(', ')}
                            </span>
                          )}
                        </span>
                        {counselor.isActive === false ? (
                          <span className="text-xs text-red-500 shrink-0">(inactive)</span>
                        ) : counselor.isAvailable === false ? (
                          <span className="text-xs text-gray-400 shrink-0">(unavailable)</span>
                        ) : null}
                      </div>
                    )
                  })}
                </div>
              ) : (
                <p className="text-sm text-gray-400">No counselors linked yet.</p>
              )}

              <div className="mt-3">
                {!showAssign ? (
                  <button
                    type="button"
                    onClick={openAssign}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                  >
                    <UserPlus className="h-4 w-4" />
                    Assign counselors
                  </button>
                ) : (
                  <div className="border border-gray-200 rounded-md p-3 bg-gray-50 space-y-3">
                    <p className="text-xs text-gray-600">
                      Counselors that don&apos;t belong to any agency yet. Assigning here is the same as
                      choosing this agency in the counselor&apos;s own form.
                    </p>

                    {loadingUnassigned ? (
                      <p className="text-sm text-gray-400">Loading…</p>
                    ) : unassigned.length === 0 ? (
                      <p className="text-sm text-gray-500">Every counselor already belongs to an agency.</p>
                    ) : (
                      <MultiSelect
                        options={unassigned.map((c) => {
                          const contact = c.email || c.user?.email
                          return {
                            value: c.id,
                            label: contact ? `${counselorDisplayName(c)} — ${contact}` : counselorDisplayName(c),
                          }
                        })}
                        selected={selectedToAssign}
                        onChange={setSelectedToAssign}
                        placeholder="Select counselors to add"
                      />
                    )}

                    {assignError && <p className="text-sm text-red-500">{assignError}</p>}

                    {assignResult && (
                      <div className="text-sm space-y-1">
                        <p className="text-green-700">
                          {assignResult.assigned} counselor{assignResult.assigned === 1 ? '' : 's'} added to this agency.
                        </p>
                        {assignResult.skipped.map((sk) => (
                          <p key={sk.id} className="text-amber-700">
                            Skipped: {sk.reason}
                          </p>
                        ))}
                      </div>
                    )}

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={handleAssign}
                        disabled={assigning || selectedToAssign.length === 0}
                        className="px-3 py-1.5 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {assigning
                          ? 'Assigning…'
                          : selectedToAssign.length > 0
                            ? `Assign ${selectedToAssign.length} counselor${selectedToAssign.length === 1 ? '' : 's'}`
                            : 'Assign'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowAssign(false)}
                        className="px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                      >
                        Done
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="text-sm text-gray-500 pt-4 border-t border-gray-200">
              <p>Created: {formatDate(agency.createdAt)}</p>
              <p>Updated: {formatDate(agency.updatedAt)}</p>
            </div>
          </div>

          <div className="flex justify-end pt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AgencyManagement